import { type Database } from "@fitness-app/supabase";

import { type FileMessageContent } from "./Chat";
import { type VideoProbeSummary, type VideoProcessingStatus } from "./VideoProcessing";

export type ExerciseRecordingEntity = Database["public"]["Tables"]["exercise_recording"]["Row"];
export type ExerciseRecordingOwnership = Database["public"]["Tables"]["exercise_recording_ownership"]["Row"];

export enum ExerciseRecordingStatus {
  PENDING = "pending",
  SCHEDULED = "scheduled",
  RECORDED = "recorded",
  RATED = "rated",
  ARCHIVED = "archived",
}

type ExerciseRecordingMetadataValue = string | number | null | boolean | undefined | VideoProbeSummary;

export type ExerciseRecordingMetadata = {
  path?: string | null;
  addedAt?: string | null;
  uploadedByTrainer?: boolean | null;
  /**
   * Set by `save-workout-for-trainee-task` when the workout carrying this exercise was fanned out
   * by a challenge. It is what separates a scored challenge entry from an ordinary video the
   * trainer asked for, and is already the discriminator used by the stale-recording archive sweep.
   */
  challengeId?: string | null;
  videoProcessingStatus?: VideoProcessingStatus | null;
  sourcePath?: string | null;
  processedPath?: string | null;
  processedAt?: string | null;
  videoProbe?: VideoProbeSummary | null;
  /** Poster frame generated alongside the transcode, so lists do not show a black tile. */
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
  videoProcessingError?: string | null;
  /**
   * When the trainers were told the upload is ready to watch.
   *
   * Deliberately not the `notificationSentAt` column: that one is stamped by
   * `scheduleExerciseRecording` when the *client* is asked to record the exercise, so it
   * is already set on every scheduled row and cannot say anything about the trainers.
   */
  trainerNotifiedAt?: string | null;
} & Record<string, ExerciseRecordingMetadataValue>;

export interface ExerciseRecording extends Omit<ExerciseRecordingEntity, "status" | "metadata"> {
  status: ExerciseRecordingStatus;
  metadata: null | ExerciseRecordingMetadata;
}

export interface ExerciseRecordingWithExerciseInfo extends ExerciseRecording {
  exercise: {
    name: string;
  };
  plan: {
    name: string;
  } | null;
  feedback: {
    createdAt: string;
    content: string;
    files: FileMessageContent[];
  } | null;
  trainee?: {
    firstName: string;
    lastName: string;
    email: string;
    trainer?: {
      firstName: string;
      lastName: string;
      userId: string;
    } | null;
  };
}

/**
 * Coerce a date-ish value to YYYY-MM-DD. `scheduledAt` is typed as a string, but kysely returns a
 * `date` column as a `Date` in server paths — the same trap `ChallengeScoreCalculator` guards.
 */
const toRecordingDay = (value: string | Date | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  return typeof value === "string" ? value.slice(0, 10) : value.toISOString().slice(0, 10);
};

/** A recording whose upload slot is not open yet, and the day it opens (YYYY-MM-DD). */
export type ExerciseRecordingUploadLock = {
  unlocksAt: string;
};

/**
 * Why a challenge video cannot be uploaded yet, or `null` when it can.
 *
 * A challenge recording task is scheduled for specific dates and `save-workout-for-trainee-task`
 * fans one row out per date up front, so the client's calendar holds the whole month at once.
 * Without a day gate she can film once and send every date in one sitting — and the score engine
 * credits each row the moment it flips to `recorded` (`qualifyingRecordingDays` filters by the
 * challenge window, not by "up to today"), so the leaderboard fills up for days that have not
 * happened.
 *
 * Two deliberate exemptions:
 *  - recordings outside a challenge (`metadata.challengeId` absent) — there the video is feedback
 *    for the trainer, not a scored entry, and filming ahead costs nobody anything,
 *  - the past — a video sent after the training day is normal and stays allowed. Only the future
 *    is locked.
 *
 * `today` must be the studio's calendar day (`getTodayInTimezone()`), NOT the device's. The dates
 * this compares against — `scheduledAt`, the challenge's own dates — are all anchored to that
 * calendar, and the database enforces the same rule in `Europe/Warsaw`; reading the day off the
 * device would let the two disagree for anyone travelling, opening the tile for a client in Tokyo
 * at 00:30 whose upload the server would then refuse.
 */
export const getExerciseRecordingUploadLock = (
  recording: Pick<ExerciseRecording, "metadata" | "scheduledAt">,
  today: string,
): ExerciseRecordingUploadLock | null => {
  if (!recording.metadata?.challengeId) {
    return null;
  }

  const scheduledDay = toRecordingDay(recording.scheduledAt);

  if (!scheduledDay || scheduledDay <= today.slice(0, 10)) {
    return null;
  }

  return { unlocksAt: scheduledDay };
};
